﻿import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { MainLayout } from '@/layouts/MainLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { ProfileLayout } from '@/layouts/ProfileLayout';
import { CheckoutLayout } from '@/layouts/CheckoutLayout';
import { PageSkeleton } from '@/components/feedback/PageSkeleton';

/* Lazy-loaded pages */
const HomePage = lazy(() => import('@/pages/HomePage'));
const ShopPage = lazy(() => import('@/pages/ShopPage'));
const CategoryPage = lazy(() => import('@/pages/CategoryPage'));
const BrandPage = lazy(() => import('@/pages/BrandPage'));
const ProductDetailsPage = lazy(() => import('@/pages/ProductDetailsPage'));
const SearchPage = lazy(() => import('@/pages/SearchPage'));
const CartPage = lazy(() => import('@/pages/CartPage'));
const WishlistPage = lazy(() => import('@/pages/WishlistPage'));
const CheckoutPage = lazy(() => import('@/pages/CheckoutPage'));

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const OtpPage = lazy(() => import('@/pages/OtpPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));

const ProfileDashboard = lazy(() => import('@/pages/ProfilePage'));
const PersonalDetailsPage = lazy(() => import('@/pages/PersonalDetailsPage'));
const AddressesPage = lazy(() => import('@/pages/AddressesPage'));
const OrdersPage = lazy(() => import('@/pages/OrdersPage'));
const OrderDetailPage = lazy(() => import('@/pages/OrderDetailPage'));
const ProfileWishlistPage = lazy(() => import('@/pages/ProfileWishlistPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));

const AboutPage = lazy(() => import('@/pages/AboutPage'));
const FaqPage = lazy(() => import('@/pages/FaqPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

export function AppRouter() {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        {/* Main Layout */}
        <Route element={<MainLayout />}>
          <Route index element={<HomePage />} />
          <Route path="shop" element={<ShopPage />} />
          <Route path="category/:slug" element={<CategoryPage />} />
          <Route path="brand/:slug" element={<BrandPage />} />
          <Route path="category/:slug/:subCategorySlug" element={<CategoryPage />} />
          <Route path="product/:slug" element={<ProductDetailsPage />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="wishlist" element={<WishlistPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="faq" element={<FaqPage />} />
          <Route path="privacy-policy" element={<PrivacyPage />} />
          <Route path="terms" element={<TermsPage />} />
        </Route>

        {/* Auth Layout */}
        <Route element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="otp-verification" element={<OtpPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* Profile Layout */}
        <Route path="profile" element={<ProfileLayout />}>
          <Route index element={<ProfileDashboard />} />
          <Route path="details" element={<PersonalDetailsPage />} />
          <Route path="addresses" element={<AddressesPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="orders/:id" element={<OrderDetailPage />} />
          <Route path="wishlist" element={<ProfileWishlistPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Checkout Layout */}
        <Route element={<CheckoutLayout />}>
          <Route path="checkout" element={<CheckoutPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

